/* F1B.2B: pure evidence verification. No automatic initialization or financial computation. */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./nikuya-cart-resolver.js'));
  else root.NikuyaCartFinancial = factory(root.NikuyaCartResolver);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (Core) {
  'use strict';
  const profile = (code, heroPercentage) => Object.freeze({ code, heroPercentage,
    expectedCaseFinalMinor: 1, currency: 'PHP', implementation: Object.freeze({
      caseMechanism: 'FIXED_AMOUNT', observedFixedAmountMinor: 39999,
      evidence: 'OWNER_MANUAL_CHECKOUT_QA_2026_09_16', managedRuntimeVerified: false }) });
  const ten = profile('NKYACQ10', 10), fifteen = profile('NKYACQ15', 15);
  const profiles = Object.freeze({ C1: ten, C2: ten, C3: ten, C4: fifteen });
  const positive = n => Number.isSafeInteger(n) && n > 0;
  const same = (line, mapping, kind) => line.sku === mapping[kind + 'Sku'] &&
    String(line.product_id) === String(mapping[kind + 'ProductId']) &&
    String(line.variant_id) === String(mapping[kind + 'VariantId']);
  const individual = line => !line.parent_relationship && !line.item_components?.length && !line.selling_plan_allocation;
  const decimal = v => (typeof v === 'number' && Number.isFinite(v) && v > 0) ||
    (typeof v === 'string' && /^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(v) && Number.isFinite(Number(v)) && Number(v) > 0);

  function verifyAcquisitionFinancialState(input = {}) {
    const result = { status: 'BLOCKED', snapshotRevision: null, profile: null,
      financialEnforcementVerified: false, casePricingVerified: false, heroes: [], cases: [],
      blockers: [], diagnostics: ['SERVER_OBSERVATION_ONLY_NOT_EXECUTION_AUTHORITY'] };
    const block = (code, key) => result.blockers.push({ code, ...(key ? { key } : {}) });
    const finish = () => {
      result.status = result.blockers.some(b => b.code.startsWith('AMBIGUOUS')) ? 'AMBIGUOUS' :
        result.blockers.length ? 'BLOCKED' : result.heroes.length ? 'VERIFIED' : 'NOT_APPLICABLE';
      result.financialEnforcementVerified = result.status === 'VERIFIED';
      result.casePricingVerified = result.status === 'VERIFIED' && result.cases.length > 0 && result.cases.every(c => c.verified);
      return result;
    };
    const { cart, acquisition, expectedProfile, managedCaseTarget, revision } = input || {};
    if (!Core || !cart || cart.authoritative !== true || typeof cart.revision !== 'string' || !cart.revision ||
        input.snapshotRevision !== cart.revision || cart.currency !== 'PHP' || input.mappingVerified !== true) {
      block('AUTHORITATIVE_SNAPSHOT_AND_MAPPING_REQUIRED'); return finish();
    }
    result.snapshotRevision = cart.revision;
    const selected = profiles[acquisition?.stage];
    if (!selected || selected.code !== expectedProfile) { block('STAGE_PROFILE_MISMATCH'); return finish(); }
    result.profile = selected.code;
    const plan = Core.planCaseReconciliation(input);
    if (plan.cases.length !== 3 || plan.blockers.some(b => b.code === 'ACQUISITION_AUTHORITY_INVALID')) {
      block('AUTHORITY_OR_MAPPING_INVALID'); return finish();
    }
    // Cart-level allocations cannot prove a product-specific acquisition allocation.
    if (cart.cart_level_discount_applications != null &&
        (!Array.isArray(cart.cart_level_discount_applications) || cart.cart_level_discount_applications.length)) {
      block('AMBIGUOUS_CART_LEVEL_APPLICATIONS');
    }
    for (const line of cart.items) {
      for (const allocation of Array.isArray(line.line_level_discount_allocations) ? line.line_level_discount_allocations : []) {
        const title = allocation?.discount_application?.title;
        if (['NKYACQ10', 'NKYACQ15'].includes(title) && title !== selected.code) block('AMBIGUOUS_ACQUISITION_APPLICATIONS', line.key);
      }
    }
    function verifyLine(line, kind) {
      const start = result.blockers.length;
      const amounts = ['original_price', 'original_line_price', 'final_price', 'final_line_price'];
      if (!positive(line.quantity) || !amounts.every(key => positive(line[key])) ||
          !positive(line.original_price * line.quantity) || !positive(line.final_price * line.quantity) ||
          line.original_line_price !== line.original_price * line.quantity ||
          line.final_line_price !== line.final_price * line.quantity || line.final_price >= line.original_price) {
        block('MALFORMED_OR_CONTRADICTORY_AMOUNTS', line.key);
      }
      if (kind === 'case' && (line.final_price !== selected.expectedCaseFinalMinor || line.final_line_price !== line.quantity)) {
        block('CASE_FINAL_PRICE_NOT_ONE_MINOR_UNIT', line.key);
      }
      const allocations = line.line_level_discount_allocations;
      if (!Array.isArray(allocations) || allocations.length !== 1) {
        block(Array.isArray(allocations) && allocations.length > 1 ? 'AMBIGUOUS_LINE_ALLOCATIONS' : 'ALLOCATION_REQUIRED', line.key);
      } else {
        const allocation = allocations[0], app = allocation?.discount_application;
        if (!positive(allocation?.amount) || !app || app.title !== selected.code ||
            (app.code != null && app.code !== selected.code) ||
            (app.type != null && app.type !== 'discount_code') ||
            (app.target_type != null && app.target_type !== 'line_item')) block('PROFILE_ALLOCATION_MISMATCH', line.key);
        if (!decimal(app?.value) || app?.value_type !== (kind === 'hero' ? 'percentage' : 'fixed_amount') ||
            (kind === 'hero' && Number(app?.value) !== selected.heroPercentage)) block('PROFILE_VALUE_MISMATCH', line.key);
        // Consistency of returned money only; no percent-derived or 400-minus-399.99 applied price.
        if (positive(allocation?.amount) && positive(line.original_line_price) && positive(line.final_line_price) &&
            allocation.amount !== line.original_line_price - line.final_line_price) block('ALLOCATION_AMOUNT_MISMATCH', line.key);
        if (line.line_level_total_discount != null && line.line_level_total_discount !== allocation?.amount) {
          block('ALLOCATION_TOTAL_MISMATCH', line.key);
        }
        if (line.discounts != null) {
          if (!Array.isArray(line.discounts) || line.discounts.some(d => !d || d.title !== selected.code || !positive(d.amount)) ||
              (line.discounts.length && line.discounts.reduce((sum, d) => sum + d.amount, 0) !== allocation?.amount)) {
            block('AMBIGUOUS_LEGACY_DISCOUNTS', line.key);
          }
        }
      }
      return { key: line.key, sku: line.sku, quantity: line.quantity,
        finalPrice: line.final_price ?? null, finalLinePrice: line.final_line_price ?? null,
        verified: result.blockers.length === start };
    }
    for (const mapping of input.mappings) {
      for (const line of cart.items.filter(item => same(item, mapping, 'hero') && individual(item))) {
        result.heroes.push(verifyLine(line, 'hero'));
      }
    }
    if (managedCaseTarget != null) {
      const state = plan.cases.find(c => c.caseSku === managedCaseTarget.caseSku);
      const mapping = input.mappings.find(m => m.caseSku === managedCaseTarget.caseSku);
      if (!state || !mapping || state.entitledQuantity < 1 || state.manualQuantity || state.protectedQuantity ||
          state.currentManagedQuantity > state.entitledQuantity) {
        block('MANAGED_CASE_OWNERSHIP_OR_ENTITLEMENT_INVALID');
      } else {
        const lines = cart.items.filter(line => state.managedKeys.includes(line.key) &&
          (managedCaseTarget.key == null || line.key === managedCaseTarget.key));
        if (!lines.length) block('MANAGED_CASE_TARGET_NOT_FOUND');
        for (const line of lines) {
          // Recognition includes all current metadata and independent ownershipVerified, via the existing planner.
          if (!same(line, mapping, 'case') || revision.acquisition !== line.properties._nikuya_acq_revision ||
              revision.mapping !== line.properties._nikuya_mapping_revision) block('MANAGED_CASE_IDENTITY_INVALID', line.key);
          else result.cases.push(verifyLine(line, 'case'));
        }
      }
    }
    return finish();
  }
  return Object.freeze({ profiles, verifyAcquisitionFinancialState });
});
